import React from 'react';

import { cssMap, styled } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import ReactRenderer from '@atlaskit/renderer/renderer';
import { token } from '@atlaskit/tokens';

import { useRenderers } from '../../../../controllers/form-renderer-store';
import { FormExtensionRenderer } from './form-extension-renderer';
import type { FormSectionProps } from './types';
import { rendererEventHandlers } from './utils';

export const FormSection = ({ sectionLayout, sectionKey }: FormSectionProps): React.JSX.Element => {
	const renderers = useRenderers();

	return (
		<Box xcss={styles.sectionContainer}>
			<ReactRendererStyleOverridesWithZIndex>
				<ReactRenderer
					key={sectionKey}
					document={sectionLayout}
					extensionHandlers={{
						'com.thinktilt.proforma': FormExtensionRenderer,
					}}
					eventHandlers={rendererEventHandlers}
					adfStage="stage0"
					nodeComponents={renderers.adfNodeRenderers}
				/>
			</ReactRendererStyleOverridesWithZIndex>
		</Box>
	);
};

const styles = cssMap({
	sectionContainer: {
		marginBottom: token('space.200'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ReactRendererStyleOverridesWithZIndex = styled.div({
	// This is required because the ReactRenderer hard codes the container style with overflow-x: auto
	// If we don't unset this style, all our dropdown menus will trigger a scrollbar to appear and
	// be hidden within the scrollable container instead of overflowing over the rest of the form
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .ak-renderer-extension-overflow-container': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		overflow: 'unset !important',
	},
	// ProseMirror tables wrap cells in `.pm-table-container` with a non-auto z-index, which creates a
	// stacking context. Portaled pickers (e.g. date/time inside a table cell) then paint *under*
	// later siblings in the form instead of above them. Forcing `z-index: auto` removes that context
	// so overlays match the rest of the renderer.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .pm-table-container': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		zIndex: 'auto !important',
	},
});
